import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { btnPrimary, btnOutline } from "../components/portfolio/styles";
import { Reveal } from "../components/portfolio/fragments";
import Seo from "../components/portfolio/Seo";

const CONTACT_URL =
  "https://mail.google.com/mail/?view=cm&fs=1&to=saidtomehedi@gmail.com";

const story = [
  {
    num: "01",
    title: "Learning by building.",
    text: [
      "Kodular was where I began. No-code development gave me a way to experiment with ideas and turn them into working apps without having to write everything from scratch.",
      "Freelancing came next. Working on different projects helped me understand that every product has its own requirements, constraints and problems to solve.",
    ],
  },
  {
    num: "02",
    title: "Seeing things differently through design.",
    text: [
      "When I started learning Figma, I began looking at apps differently. I paid more attention to layouts, spacing, typography and how information is presented.",
      "I started recreating and refining interfaces instead of settling for the first version. I also explored coding and React Native, which helped me understand what happens between a design and a working app.",
    ],
  },
  {
    num: "03",
    title: "Building something of my own.",
    text: [
      "StockMate was one of the projects that shaped my approach. I designed and built it as an offline-first inventory and billing app for small retailers, with features for managing products, sales, purchases, expenses and customer balances.",
      "Testing the app myself, talking with shop owners and seeing it used in real shops helped me understand the difference between building a feature and making something useful in everyday work.",
      "Later, I developed Storepilot into a broader shop-management product. Working on both gave me the opportunity to keep exploring the product while paying closer attention to its visual design and consistency.",
    ],
  },
  {
    num: "04",
    title: "My approach today.",
    text: [
      "I design in Figma and build with AI tools. I guide the direction, make the product decisions and refine the interface into a consistent design system.",
      "AI helps me move from an idea to a working product faster, but the decisions stay mine: what the product should do, how it should feel and what needs to improve.",
      "Every product I ship sharpens how I design the next one. That feedback loop is where I've grown the most.",
    ],
  },
];

const cares = [
  { title: "CLARITY", text: "Making screens easier to understand and use.", span: "md:col-span-3" },
  { title: "CONSISTENCY", text: "Making an app feel like one connected product, not a collection of unrelated screens.", span: "md:col-span-3" },
  { title: "USEFUL DETAILS", text: "Paying attention to the small things that make an everyday task easier.", span: "md:col-span-2" },
  { title: "ITERATION", text: "Being willing to revisit a design when there's a better way to approach it.", span: "md:col-span-2" },
  { title: "PRACTICALITY", text: "Remembering that a design needs to work in a real product, not only in Figma.", span: "md:col-span-2" },
];

const designSkills = ["Product Thinking", "UX/UI Design", "Interaction Design", "Prototyping", "Design Systems"];
const tools = ["Figma", "FigJam", "Framer", "Notion", "AI Coding Agents"];

export default function About() {
  return (
    <div className="max-w-4xl mx-auto" data-testid="about-page">
      <Seo
        title="About Mehedi Hassain | Product Designer from Kolkata, India"
        description="How Mehedi Hassain went from building no-code apps to designing and shipping StockMate and Storepilot — his approach, design skills, tools and what he's looking for next."
        path="/about"
      />
      {/* ABOUT HERO */}
      <section className="pt-10 md:pt-16 pb-20 space-y-12" data-testid="about-hero">
        <Reveal>
          <div className="flex items-center gap-4">
            <p className="text-sm font-semibold tracking-[0.25em] text-primary shrink-0">ABOUT ME</p>
            <span className="flex-1 border-t border-border" aria-hidden="true" />
            <span className="text-sm text-muted-foreground shrink-0">Mehedi Hassain</span>
          </div>
        </Reveal>

        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[1.04] tracking-[-0.02em] text-foreground" data-testid="about-headline">
          {["I started by building apps.", "Along the way, I found", "product design."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? <em className="italic text-primary">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="grid md:grid-cols-[auto_1fr] gap-10 md:gap-14 items-start">
          <Reveal delay={0.35}>
            <figure className="space-y-3">
              <img
                src="/profile.jpg"
                alt="Portrait of Mehedi Hassain, Product Designer"
                className="w-52 md:w-64 aspect-[4/5] object-cover rounded-[1.5rem] border border-border shadow-xl rotate-1 hover:rotate-0 transition-transform duration-500"
                data-testid="about-portrait"
              />
              <figcaption className="text-sm text-muted-foreground">
                Mehedi Hassain — Product Designer
              </figcaption>
            </figure>
          </Reveal>
          <div className="space-y-8 md:pt-2">
            <Reveal delay={0.45}>
              <div className="text-lg text-muted-foreground leading-relaxed max-w-xl space-y-4">
                <p className="text-xl text-foreground font-medium leading-relaxed">
                  I'm Mehedi Hassain, a Product Designer who likes taking an idea all the way —
                  from the first sketch to something real people use.
                </p>
                <p className="text-base">
                  I started building apps in 2022 with Kodular. At first, I was focused on making
                  things work. Through freelance projects, I explored different ideas, learned
                  Figma and began paying more attention to how an app looks and feels, not just
                  what it does.
                </p>
                <p className="text-base">
                  That shift changed how I approached my own products. Since then, I've designed
                  and shipped two live Android products — StockMate and Storepilot — owning
                  everything from user research and flows to the design system and the final UI.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground" data-testid="about-meta">
                <span className="inline-flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                  Open to Product Designer opportunities
                </span>
                <span className="text-border" aria-hidden="true">·</span>
                <span>Based in India</span>
                <span className="text-border" aria-hidden="true">·</span>
                <a href="mailto:saidtomehedi@gmail.com" className="hover:text-primary transition-colors" data-testid="about-meta-email">
                  saidtomehedi@gmail.com
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW I GOT HERE */}
      <section className="py-20 md:py-28 border-t border-border space-y-14" data-testid="about-story">
        <Reveal>
          <p className="text-sm font-semibold tracking-[0.25em] text-primary">HOW I GOT HERE</p>
        </Reveal>
        <div className="border-l-2 border-border pl-8 md:pl-12 space-y-16">
          {story.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.05}>
              <div className="relative" data-testid={`story-${s.num}`}>
                <span className="absolute -left-[41px] md:-left-[57px] top-2 w-4 h-4 rounded-full bg-background border-2 border-primary" aria-hidden="true" />
                <span className="font-display text-2xl text-primary/30">{s.num}</span>
                <h3 className="font-display text-2xl md:text-4xl text-foreground leading-tight mt-1 mb-3">
                  {s.title}
                </h3>
                <div className="space-y-4 max-w-2xl">
                  {s.text.map((p, j) => (
                    <p key={j} className="text-muted-foreground leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT MATTERS TO ME */}
      <section className="py-20 md:py-28 border-t border-border space-y-12" data-testid="about-cares">
        <Reveal>
          <p className="text-sm font-semibold tracking-[0.25em] text-primary">WHAT MATTERS TO ME</p>
        </Reveal>
        <div className="grid md:grid-cols-6 gap-4">
          {cares.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05} className={c.span}>
              <div className="rounded-2xl border border-border p-6 md:p-8 h-full space-y-3 hover:border-primary/40 hover:bg-primary/5 transition-all duration-300" data-testid={`care-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="text-xs font-bold tracking-[0.2em] text-primary">{c.title}</p>
                <p className="font-display text-xl md:text-2xl text-foreground leading-snug">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SKILLS + TOOLS */}
      <section className="py-20 md:py-28 border-t border-border space-y-10" data-testid="about-design-tech">
        <Reveal>
          <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-foreground max-w-3xl">
            Design, with an understanding of{" "}
            <em className="italic text-primary">how things get built.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="space-y-4 max-w-2xl">
            <p className="text-muted-foreground leading-relaxed">
              I've spent the last few years designing in Figma and shipping my own products with
              the help of AI coding tools — with hands-on exposure to React Native and no-code
              development along the way.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Knowing how things get built helps me design what can realistically ship — and ship
              it faster.
            </p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-10">
          <Reveal delay={0.1}>
            <p className="text-xs font-bold tracking-[0.2em] text-muted-foreground mb-4">DESIGN SKILLS</p>
            <div className="flex flex-wrap gap-2" data-testid="design-skills">
              {designSkills.map((s) => (
                <span key={s} className="px-4 py-2 rounded-full text-sm font-medium border border-primary/25 bg-primary/5 text-primary">
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs font-bold tracking-[0.2em] text-muted-foreground mb-4">TOOLS &amp; TECHNOLOGIES</p>
            <div className="flex flex-wrap gap-2" data-testid="tech-skills">
              {tools.map((s) => (
                <span key={s} className="px-4 py-2 rounded-full text-sm font-medium border border-border text-foreground">
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT I'M LOOKING FOR */}
      <section className="py-20 border-t border-border" data-testid="about-looking-for">
        <Reveal>
          <div className="rounded-3xl bg-primary/5 border border-primary/10 p-8 md:p-10 space-y-6">
            <p className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.25em] text-primary">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              WHAT I'M LOOKING FOR
            </p>
            <div className="space-y-4 max-w-2xl">
              <p className="text-foreground leading-relaxed">
                I'm looking for a Product Designer role where I can own meaningful product problems
                — from discovery and user flows to design systems and shipped UI.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                I bring end-to-end experience designing and shipping my own products — research,
                design systems and iteration driven by real usage — plus the technical
                understanding to design what can actually be built. I'd like to work with people
                who care about the details, share feedback openly and want to make their products
                better.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* OUTSIDE OF WORK */}
      <section className="py-20 border-t border-border space-y-6" data-testid="about-beyond">
        <Reveal>
          <p className="text-sm font-semibold tracking-[0.25em] text-primary">OUTSIDE OF WORK</p>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="font-display text-2xl md:text-3xl leading-snug text-foreground max-w-3xl">
            I'm curious about technology, apps and the little details that make digital products
            enjoyable to use. I often find myself noticing how an app works and thinking about what
            could be done differently.
          </p>
        </Reveal>
      </section>

      {/* FINAL ABOUT CTA */}
      <section className="py-24 md:py-32 border-t border-border text-center space-y-8" data-testid="about-final-cta">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-[-0.02em] text-foreground">
            Have something <em className="italic text-primary">interesting to build?</em>
          </h2>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="text-muted-foreground max-w-xl mx-auto">
            I'd love to hear about the product you're working on and the problems you're trying to
            solve.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/projects" className={btnPrimary} data-testid="about-cta-work">
              View my work
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="/Mehedi-Hassain-Resume.pdf" target="_blank" rel="noopener noreferrer" className={btnOutline} data-testid="about-cta-resume">
              Download my resume
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className={btnOutline} data-testid="about-cta-talk">
              Get in touch
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
